/**
 * The pricing banner under the drug list (S-02) and on the OPR Pricing tab
 * (S-03). Pure functions so both screens, and the tests, agree.
 *
 * PR-007: ship to patient drugs are priced per month (PT-141 per bottle),
 * in clinic kits per visit, and the monthly total and the per visit extra are
 * shown separately. PR-009: the plan cannot go until every drug has a price.
 * PR-010: "est." whenever a cost is a reference cost rather than the clinic's
 * own. Costs and margins are clinic only (PG-006) and never leave this side.
 */

export type BillingUnit = 'month' | 'visit' | 'bottle';

export interface BannerLine {
  unit: BillingUnit;
  priceCents: number | null;
  costCents: number | null;
  /** True when the cost is a reference cost, not the clinic's own. */
  estimated: boolean;
}

export interface Banner {
  monthlyCents: number;
  perVisitCents: number;
  marginMonthlyCents: number | null;
  marginPerVisitCents: number | null;
  marginEstimated: boolean;
  allSet: boolean;
  missing: number;
  status: (firstName: string) => string;
}

/** Board 01: the banner's line while any box is empty. The send button reads the first sentence. */
export const SET_EVERY_PRICE = 'Set every price to send. Empty boxes show the OPR price: press Use or type your own.';

export const UNIT_LABEL:Record<BillingUnit, string> = { month: '/mo', visit: '/visit', bottle: '/bottle' };

export function pricingBanner(lines: BannerLine[]): Banner {
  let monthly = 0;
  let perVisit = 0;
  let marginMonthly = 0;
  let marginPerVisit = 0;
  let monthlyMarginKnown = false;
  let visitMarginKnown = false;
  let estimated = false;
  let missing = 0;

  for (const line of lines) {
    if (line.priceCents == null) {
      missing += 1;
      continue;
    }
    const isVisit = line.unit === 'visit';
    if (isVisit) perVisit += line.priceCents;
    else monthly += line.priceCents;
    if (line.costCents != null) {
      const margin = line.priceCents - line.costCents;
      if (isVisit) {
        marginPerVisit += margin;
        visitMarginKnown = true;
      } else {
        marginMonthly += margin;
        monthlyMarginKnown = true;
      }
      if (line.estimated) estimated = true;
    }
  }

  const allSet = lines.length > 0 && missing === 0;
  return {
    monthlyCents: monthly,
    perVisitCents: perVisit,
    marginMonthlyCents: monthlyMarginKnown ? marginMonthly : null,
    marginPerVisitCents: visitMarginKnown ? marginPerVisit : null,
    marginEstimated: estimated,
    allSet,
    missing,
    status: (firstName: string) => (allSet ? `All prices set. ${firstName} sees these exact prices.` : SET_EVERY_PRICE),
  };
}